import { IKpiAppUsage, KpiAppUsagePlatform } from '@/types/response';
import moment from 'moment';
import { IPeriod, periodKey, periodsOf } from './appUsage';
import { labelOf } from './labels';

/**
 * 新規ファネル（AppsFlyer のインストール週ごと）。
 *
 * 1週 = その週（月曜〜日曜、UTC）にインストールした端末。値はインストール後7日以内に
 * そのイベントを起こした端末数で、iOS / Android を別の行で持つ。
 * 合計は iOS と Android を足す（端末が重ならないので足してよい）。
 */
export const COHORT_SOURCE = 'appsflyer_cohort' as const;

export const INSTALL = 'install';

/** ステップの並び。各ステップは「7日以内にそのイベントを起こした人数」で、前のステップを通ったかは問わない */
export const FUNNEL_STEPS: { name: string; label: string }[] = [
  { name: INSTALL, label: 'インストール（初回起動）' },
  { name: 'af_login', label: '利用開始（ゲストでサインイン）' },
  { name: 'af_complete_registration', label: 'プロフィール登録' },
  { name: 'family_group_created', label: '家族グループを作成' },
  { name: 'diary_post_tapped', label: '「日記を書く」をタップ' },
  { name: 'diary_published', label: '日記を公開' },
  { name: 'af_invite', label: '招待リンクを共有' },
  { name: 'family_connected', label: '家族が2人以上に' },
];

/** 継続率。値はその日（インストールの翌日・7日後）に起動した人数 */
export const RETENTION: { name: string; label: string }[] = [
  { name: 'retention_d1', label: '翌日も起動' },
  { name: 'retention_d7', label: '7日後も起動' },
];

const FIXED_NAMES = new Set([...FUNNEL_STEPS, ...RETENTION].map((s) => s.name));

/** 推移グラフで選べる指標（すべてインストールに対する割合） */
export const TREND_METRICS: { name: string; label: string }[] = [
  ...FUNNEL_STEPS.filter((s) => s.name !== INSTALL),
  ...RETENTION,
];

export const labelOfCohort = (name: string): string =>
  [...FUNNEL_STEPS, ...RETENTION].find((s) => s.name === name)?.label ??
  labelOf(COHORT_SOURCE, name).label;

/** 割合（%、小数1桁）。分母が 0 なら null */
export const pct = (n: number, d: number): number | null =>
  d ? Math.round((n / d) * 1000) / 10 : null;

type Values = Map<string, { users: number; count: number }>;

/** 1週分の値（name → 人数・イベント数）。platform = 'all' は iOS と Android の合計 */
export const cohortValues = (
  rows: IKpiAppUsage[],
  period: { periodStart: string; periodEnd: string },
  platform: KpiAppUsagePlatform,
): Values => {
  const key = periodKey({ source: COHORT_SOURCE, ...period });
  const values: Values = new Map();
  rows
    .filter(
      (r) =>
        periodKey(r) === key && (platform === 'all' || r.platform === platform),
    )
    .forEach((r) => {
      const v = values.get(r.name) ?? { users: 0, count: 0 };
      values.set(r.name, {
        users: v.users + r.users,
        count: v.count + r.count,
      });
    });

  return values;
};

/** 前の週（7日前に始まる週）。取り込みが無ければ null */
export const previousWeekOf = (
  rows: IKpiAppUsage[],
  period: IPeriod,
): IPeriod | null => {
  const start = moment(period.periodStart, 'YYYY-MM-DD')
    .subtract(7, 'days')
    .format('YYYY-MM-DD');

  return (
    periodsOf(rows, COHORT_SOURCE).find((p) => p.periodStart === start) ?? null
  );
};

export interface IFunnelStep {
  name: string;
  label: string;
  users: number;
  /** インストールに対する割合 */
  installPct: number | null;
  /** ひとつ前のステップに対する割合（最初のステップは null） */
  prevStepPct: number | null;
  /** 前の週のインストール比との差（ポイント）。前の週が無ければ null */
  installPctDiff: number | null;
}

const diffPt = (cur: number | null, prev: number | null): number | null =>
  cur === null || prev === null ? null : Math.round((cur - prev) * 10) / 10;

const installsOf = (values: Values) => values.get(INSTALL)?.users ?? 0;

export const funnelSteps = (
  values: Values,
  prevValues: Values | null,
): IFunnelStep[] => {
  const installs = installsOf(values);
  const prevInstalls = prevValues ? installsOf(prevValues) : 0;

  return FUNNEL_STEPS.map((step, i) => {
    const users = values.get(step.name)?.users ?? 0;
    const before =
      i === 0 ? null : (values.get(FUNNEL_STEPS[i - 1].name)?.users ?? 0);
    const installPct = pct(users, installs);
    const prevInstallPct = prevValues
      ? pct(prevValues.get(step.name)?.users ?? 0, prevInstalls)
      : null;

    return {
      ...step,
      users,
      installPct,
      prevStepPct: before === null ? null : pct(users, before),
      // インストールは常に 100% なので差を出さない
      installPctDiff: i === 0 ? null : diffPt(installPct, prevInstallPct),
    };
  });
};

export interface IRetention {
  name: string;
  label: string;
  users: number;
  installPct: number | null;
  installPctDiff: number | null;
}

export const retentions = (
  values: Values,
  prevValues: Values | null,
): IRetention[] => {
  const installs = installsOf(values);

  return RETENTION.map((r) => {
    const users = values.get(r.name)?.users ?? 0;
    const installPct = pct(users, installs);

    return {
      ...r,
      users,
      installPct,
      installPctDiff: prevValues
        ? diffPt(
            installPct,
            pct(prevValues.get(r.name)?.users ?? 0, installsOf(prevValues)),
          )
        : null,
    };
  });
};

export interface IOtherEvent {
  name: string;
  label: string;
  users: number;
  count: number;
  installPct: number | null;
}

/** ファネル以外の機能（7日以内に使った人の割合、多い順） */
export const otherEvents = (values: Values): IOtherEvent[] => {
  const installs = installsOf(values);

  return Array.from(values.entries())
    .filter(([name]) => !FIXED_NAMES.has(name))
    .map(([name, v]) => ({
      name,
      label: labelOf(COHORT_SOURCE, name).label,
      users: v.users,
      count: v.count,
      installPct: pct(v.users, installs),
    }))
    .sort((a, b) => b.users - a.users || a.name.localeCompare(b.name));
};

/**
 * 週ごとのインストール比（%）の推移。recharts にそのまま渡す形
 * `[{ week: '10/05', af_login: 61.7, ... }]`。取り込みの無い週も軸に残す（値は持たない）。
 */
export const weeklyTrend = (
  rows: IKpiAppUsage[],
  platform: KpiAppUsagePlatform,
  names: string[],
): Record<string, string | number>[] => {
  const weeks = periodsOf(rows, COHORT_SOURCE).reverse();
  if (weeks.length === 0) return [];

  const byStart = new Map(weeks.map((p) => [p.periodStart, p]));
  const last = moment(weeks[weeks.length - 1].periodStart, 'YYYY-MM-DD');
  const points: Record<string, string | number>[] = [];
  for (
    const w = moment(weeks[0].periodStart, 'YYYY-MM-DD');
    !w.isAfter(last);
    w.add(7, 'days')
  ) {
    const point: Record<string, string | number> = { week: w.format('MM/DD') };
    const p = byStart.get(w.format('YYYY-MM-DD'));
    if (p) {
      const values = cohortValues(rows, p, platform);
      const installs = installsOf(values);
      names.forEach((name) => {
        const v = pct(values.get(name)?.users ?? 0, installs);
        if (v !== null) point[name] = v;
      });
    }
    points.push(point);
  }

  return points;
};
